import { createCrudStore } from "@/store/crudStore";
import { apiService } from "@/services/api";

const base = createCrudStore("pasturas");

const pasturasStore = {
  ...base,

  state: {
    ...base.state,
    eventos: [],
    cargaAnimal: [],
    rindeCalc: null,
    costosCalc: null,
    relacionAnimal: null,
    balanceDinamico: null,
  },

  getters: {
    ...base.getters,
    pasturas(state) {
      return Array.isArray(state.pasturas) ? state.pasturas : [];
    },
    pasturasActivas(state) {
      return (state.pasturas || []).filter((p) => p.activa !== false);
    },
    eventos: (state) => state.eventos,
    cargaAnimal: (state) => state.cargaAnimal,
    rindeCalc: (state) => state.rindeCalc,
    costosCalc: (state) => state.costosCalc,
    relacionAnimal: (state) => state.relacionAnimal,
    balanceDinamico: (state) => state.balanceDinamico,
  },

  mutations: {
    ...base.mutations,
    setEventos: (state, data) => (state.eventos = data),
    setCargaAnimal: (state, data) => (state.cargaAnimal = data),
    setRindeCalc: (state, data) => (state.rindeCalc = data),
    setCostosCalc: (state, data) => (state.costosCalc = data),
    setRelacionAnimal: (state, data) => (state.relacionAnimal = data),
    setBalanceDinamico: (state, data) => (state.balanceDinamico = data),
    clearState(state) {
      state.pasturas = [];
      state.eventos = [];
      state.cargaAnimal = [];
      state.rindeCalc = null;
      state.costosCalc = null;
      state.relacionAnimal = null;
      state.balanceDinamico = null;
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    ...base.actions,

    // ── CRUD PASTURAS ────────────────────────────────
    getPasturas: base.actions.getAllStates,

    async postPastura({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.create("pasturas", payload),
        onSuccessCallback: async (commit, data) => {
          commit("addItem", data);
          await dispatch("getPasturas");
        },
        errorMessage: "Error al crear pastura",
      });
    },

    async patchPastura({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.update("pasturas", payload.id, payload),
        onSuccessCallback: async (commit, data) => {
          commit("updateItem", data);
          await dispatch("getPasturas");
        },
        errorMessage: "Error al editar pastura",
      });
    },

    async deletePastura({ dispatch }, pasturaId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.delete("pasturas", pasturaId),
        onSuccessCallback: async (commit) => {
          commit("removeItem", pasturaId);
          await dispatch("getPasturas");
        },
        errorMessage: "Error al eliminar pastura",
      });
    },

    // ── EVENTOS ──────────────────────────────────────
    async getEventos({ dispatch, commit }, pasturaId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`pasturas/${pasturaId}/eventos`),
        onSuccessCallback: (_, data) => commit("setEventos", data),
        errorMessage: "Error al cargar eventos",
      });
    },

    async postEvento({ dispatch }, { pasturaId, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`/pasturas/${pasturaId}/eventos`, payload),
        onSuccessCallback: async () => {
          await dispatch("getPasturas");
          await dispatch("getEventos", pasturaId);
        },
        errorMessage: "Error al registrar evento",
      });
    },

    // ── CARGA ANIMAL ─────────────────────────────────
    async getCargaAnimal({ dispatch, commit }, pasturaId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`pasturas/${pasturaId}/carga-animal`),
        onSuccessCallback: (_, data) => commit("setCargaAnimal", data),
        errorMessage: "Error al cargar registros de carga animal",
      });
    },

    async postCargaAnimal({ dispatch }, { pasturaId, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`/pasturas/${pasturaId}/carga-animal`, payload),
        onSuccessCallback: async () => {
          await dispatch("getCargaAnimal", pasturaId);
        },
        errorMessage: "Error al registrar carga animal",
      });
    },

    // ── DEGRADACIÓN ──────────────────────────────────
    async registrarDegradacion(
      { dispatch },
      { pasturaId, porcentaje, observaciones }
    ) {
      const qs = new URLSearchParams({ porcentaje });
      if (observaciones) qs.append("observaciones", observaciones);
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(
            `pasturas/${pasturaId}/degradacion?${qs.toString()}`,
            {}
          ),
        onSuccessCallback: async () => {
          await dispatch("getPasturas");
        },
        errorMessage: "Error al registrar degradación",
      });
    },

    // ── CÁLCULOS ─────────────────────────────────────
    async fetchRinde({ commit }, pasturaId) {
      try {
        const res = await apiService.getCustom(
          `pasturas/${pasturaId}/calculos/rinde`
        );
        commit("setRindeCalc", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando rinde:", e);
      }
    },

    async fetchCostos({ commit }, { pasturaId, monedaBase = "peso" }) {
      try {
        const res = await apiService.getCustom(
          `pasturas/${pasturaId}/calculos/costos?moneda_base=${monedaBase}`
        );
        commit("setCostosCalc", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando costos:", e);
      }
    },

    async fetchRelacionAnimal({ commit }, { pasturaId, params = {} }) {
      try {
        const qs = new URLSearchParams();
        if (params.cantidad_animales)
          qs.append("cantidad_animales", params.cantidad_animales);
        if (params.categoria_animal)
          qs.append("categoria_animal", params.categoria_animal);
        if (params.ev_totales) qs.append("ev_totales", params.ev_totales);
        const url = `pasturas/${pasturaId}/calculos/relacion-animal${
          qs.toString() ? "?" + qs.toString() : ""
        }`;
        const res = await apiService.getCustom(url);
        commit("setRelacionAnimal", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando relación animal:", e);
      }
    },

    async fetchBalanceDinamico({ commit }, pasturaId) {
      try {
        const res = await apiService.getCustom(
          `pasturas/${pasturaId}/balance-dinamico`
        );
        commit("setBalanceDinamico", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando balance dinámico:", e);
      }
    },

    async fetchRindeRealPastura(_, pasturaId) {
      try {
        const res = await apiService.getCustom(
          `pasturas/${pasturaId}/rinde-real`
        );
        return res.data;
      } catch (e) {
        console.error("Error cargando rinde real:", e);
        return null;
      }
    },

    // ============ LLUVIAS ============
    async getLluvias(_, pasturaId) {
      const { data } = await apiService.getCustom(
        `pasturas/${pasturaId}/lluvias`
      );
      return data;
    },

    async sincronizarLluviasDesdeLote({ dispatch }, pasturaId) {
      const { data } = await apiService.postCustom(
        `pasturas/${pasturaId}/sincronizar-lluvias-lote`,
        {}
      );
      await dispatch("getPasturas");
      return data;
    },

    async postLluvia({ dispatch }, { pasturaId, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`pasturas/${pasturaId}/lluvias`, payload),
        onSuccessCallback: async () => {
          await dispatch("getPasturas");
          console.log("✅ Lluvia registrada");
        },
        errorMessage: "Error al registrar lluvia",
      });
    },

    async deleteLluvia({ dispatch }, lluviaId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`pasturas/lluvias/${lluviaId}`),
        onSuccessCallback: async () => {
          await dispatch("getPasturas");
          console.log("✅ Lluvia eliminada");
        },
        errorMessage: "Error al eliminar lluvia",
      });
    },
  },
};

export default pasturasStore;
